import React from 'react'
import { useNavigate } from 'react-router-dom'
import { CreditApplication } from '_gql'

import useDidMount from '@divvy-web/hooks.usedidmount'
import { FormattedMessage } from '@divvy-web/i18n'
import { BannerType } from '@divvy-web/skylab.banner'
import BasicButton, {
  BASIC_BUTTON_COLOR_ACTION,
  BASIC_BUTTON_SIZE_SMALL,
  BASIC_BUTTON_TYPE_OUTLINED,
} from '@divvy-web/skylab.basicbutton'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import { useAuth } from '@sputnik/auth'
import StatusBanner from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/ApplicationDetailsTabContent/ActionsNeededSection/StatusBanner'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { useRefreshCreditApplication } from './RefreshCreditApplication.gql'

interface RefreshApplicationBannerProps {
  salesforceCreditId: CreditApplication['salesforceCreditId']
}

// this tile shows for statuses: CLOSED_LOST, EXPIRED, CLOSED_AS_DUPLICATE, CUSTOMER_CANCELED
const RefreshApplicationBanner = ({ salesforceCreditId }: RefreshApplicationBannerProps) => {
  const navigate = useNavigate()
  const { setToken } = useAuth()
  const showToast = useToast()
  const { track } = useWebTracking()

  useDidMount(() => {
    track('se_signup__application_dashboard_action_needed_refresh_application__presented')
  })

  const [refreshCreditApplication, { loading: isCloningApplication }] = useRefreshCreditApplication({
    variables: { salesforceCreditId },
    onCompleted: (data) => {
      const newToken = data?.cloneCreditApplication?.token || ''
      setToken(newToken)
      const clonedCreditId = data?.cloneCreditApplication?.salesforceCreditId

      logInfo('refreshCreditApplication', {
        attributes: {
          originalCreditAppId: salesforceCreditId,
          newCreditAppId: clonedCreditId,
          result: 'Successfully refreshed credit application',
        },
        eventName: 'refreshCreditApplication',
        message: `Original Credit App ID: ${salesforceCreditId}, new Credit App Id: ${clonedCreditId} `,
      })

      navigate(`/app/${clonedCreditId}/${PATHNAME_CONSTS.NAME_AND_OWNERSHIP_PATH}`)
    },
    onError: (e) => {
      logError(e, {
        attributes: {
          action: 'refreshCreditApplication',
          creditApplicationId: salesforceCreditId,
          result: 'Error occured when refreshing credit application',
        },
        eventName: 'refreshCreditApplication',
      })
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Error refreshing credit application.'
          id='sputnik.RefreshApplicationBanner__KnZAEc'
        />,
        {
          className: 'fs-unmask',
          autoHideDelay: 5000,
        },
      )
    },
  })

  return (
    <div className='fs-unmask'>
      <StatusBanner
        bannerType={BannerType.Error}
        subtext={
          <FormattedMessage
            defaultMessage='Your application expired. <button>Refresh to continue</button>'
            id='sputnik.RefreshApplicationBanner__dezVS5'
            values={{
              button: (chunks) => (
                <BasicButton
                  color={BASIC_BUTTON_COLOR_ACTION}
                  disabled={isCloningApplication}
                  size={BASIC_BUTTON_SIZE_SMALL}
                  type={BASIC_BUTTON_TYPE_OUTLINED}
                  onClick={() => {
                    track('se_signup__application_dashboard_action_needed_refresh_to_continue__fired')
                    refreshCreditApplication()
                  }}
                >
                  {chunks}
                </BasicButton>
              ),
            }}
          />
        }
        title={
          <FormattedMessage
            defaultMessage='Refresh application'
            id='sputnik.RefreshApplicationBanner__1JRfgv'
          />
        }
      />
    </div>
  )
}

export default RefreshApplicationBanner
